import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { ApiResponseDTO } from '../../../dto/api';
import { getAttendeeId } from '../../../utils/webStorage/attendeeIdStorage';
import api from '../../../api';

function usePreferencesForm() {
  const [formSchema, setFormSchema] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data with prefilled values when schema loads
  useEffect(() => {
    if (formSchema && Object.keys(formData).length === 0) {
      const prefilledData = {};

      formSchema.steps.forEach((step) => {
        step.sections.forEach((section) => {
          section.questions.forEach((question) => {
            if (question.values && question.values.length > 0) {
              prefilledData[question.questionId] = question.values;
            }
          });
        });
      });

      if (Object.keys(prefilledData).length > 0) {
        setFormData(prefilledData);
      }
    }
  }, [formSchema]);

  useEffect(() => {
    const getFormSchema = async () => {
      try {
        setLoading(true);
        const response = await api.ConstellationDinners.getConstellationDinnerForm(getAttendeeId() as string);
        // const response = await api.ConstellationDinners.getConstellationDinnerForm('6866db53608f745fa9d756d9');

        const responseData = response.data as any;
        if (responseData.success === true && responseData.data) {
          setFormSchema(responseData.data);
        } else {
          console.error('Error:', responseData.errors);
          setError('Failed to load form schema');
        }
      } catch (error) {
        const errorObj = error as AxiosError<ApiResponseDTO>;
        console.error('Error: ', errorObj);
        setError('Failed to load form schema');
      } finally {
        setLoading(false);
      }
    };

    getFormSchema();
  }, []);

  // Validate a single field
  const validateField = (question, value) => {
    const errors = [] as any;

    // Check required field first
    if (question.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      errors.push('This field is required');
      return errors; // Return early if required field is empty
    }

    // Only validate format if field has a value or is required
    if (value && (question.type === 'TEXT' || question.type === 'PARAGRAPH')) {
      const textVal = question.textValidation;

      // Check length requirements
      if (textVal.minLength > 0 && value.length < textVal.minLength) {
        errors.push(`Minimum ${textVal.minLength} characters required`);
      }

      if (textVal.maxLength > 0 && value.length > textVal.maxLength) {
        errors.push(`Maximum ${textVal.maxLength} characters allowed`);
      }

      // Check regex patterns
      if (textVal.regex && textVal.regex.length > 0) {
        textVal.regex.forEach((regexObj) => {
          const regex = new RegExp(regexObj.regex);
          if (!regex.test(value)) {
            errors.push(regexObj.message);
          }
        });
      }
    }

    return errors;
  };

  // Validate entire form - call this on submit
  const validateForm = () => {
    if (!formSchema) return { isValid: false, errors: {} };

    const errors = {};
    let isValid = true;

    formSchema.steps.forEach((step) => {
      step.sections.forEach((section) => {
        section.questions.forEach((question) => {
          const value = formData[question.questionId];
          const actualValue = Array.isArray(value) ? value[0] : value;
          const fieldErrors = validateField(question, actualValue);

          if (fieldErrors.length > 0) {
            errors[question.questionId] = fieldErrors;
            isValid = false;
          }
        });
      });
    });

    setValidationErrors(errors);
    return { isValid, errors };
  };

  // Clear validation error for a specific field
  const clearFieldError = (questionId) => {
    if (validationErrors[questionId]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  // Clear validation errors
  const clearValidationErrors = () => {
    setValidationErrors({});
  };

  // Create clean API payload (only key-value pairs)
  const getApiPayload = () => {
    return Object.keys(formData).reduce((acc, key) => {
      acc[key] = formData[key];
      return acc;
    }, {});
  };

  // Submit form
  const submitForm = async () => {
    const validation = validateForm();

    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    try {
      setLoading(true);
      setError(null);

      const payload = getApiPayload();
      const response = await api.ConstellationDinners.submitConstellationDinnerForm(getAttendeeId() as string, payload);
      // const response = await api.ConstellationDinners.submitConstellationDinnerForm(
      //   '6866db53608f745fa9d756d9',
      //   payload
      // );

      const responseData = response.data as any;
      if (responseData.success === true && responseData.data) {
        clearValidationErrors();
        return { success: true, data: responseData.data };
      } else {
        setError('Submission failed');
        return { success: false, errors: responseData.errors };
      }
    } catch (error) {
      const errorObj = error as AxiosError<ApiResponseDTO>;
      console.error('Error: ', errorObj);
      setError('Submission failed');
      return { success: false, errors: [errorObj.message] };
    } finally {
      setLoading(false);
    }
  };

  return {
    formSchema,
    formData,
    setFormData,
    validationErrors,
    loading,
    error,
    validateForm,
    clearValidationErrors,
    clearFieldError,
    submitForm,
    getApiPayload,
  };
}

export default usePreferencesForm;
